import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  "Balkon",
  "Terrasse",
  "Treppe",
  "Eingangsbereich",
  "Gehwege",
  "Innenräume",
  "Anderes",
];

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", area: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Bitte füllen Sie Name, E-Mail und Nachricht aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        area: form.area || null,
        message: form.message.trim(),
      });
      toast.success("Vielen Dank! Wir melden uns kurzfristig bei Ihnen.");
      setForm({ name: "", email: "", phone: "", area: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "w-full bg-transparent border-0 border-b border-stone-300 focus:border-[#1C1C1C] outline-none py-3 text-[15px] placeholder:text-stone-400 transition-colors";

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className={`grid gap-6 ${compact ? "" : "max-w-2xl"}`}
    >
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500">Name *</label>
          <input
            data-testid="contact-input-name"
            className={fieldCls}
            value={form.name}
            onChange={update("name")}
            placeholder="Ihr Name"
            required
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500">E-Mail *</label>
          <input
            data-testid="contact-input-email"
            type="email"
            className={fieldCls}
            value={form.email}
            onChange={update("email")}
            placeholder="ihre@email.de"
            required
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500">Telefon</label>
          <input
            data-testid="contact-input-phone"
            className={fieldCls}
            value={form.phone}
            onChange={update("phone")}
            placeholder="+49 ..."
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500">Bereich</label>
          <select
            data-testid="contact-select-area"
            className={`${fieldCls} appearance-none cursor-pointer`}
            value={form.area}
            onChange={update("area")}
          >
            <option value="">Bitte wählen</option>
            {AREAS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] text-stone-500">Nachricht *</label>
        <textarea
          data-testid="contact-input-message"
          className={`${fieldCls} resize-none`}
          rows={5}
          value={form.message}
          onChange={update("message")}
          placeholder="Beschreiben Sie kurz Ihr Projekt (Fläche, Bereich, Wünsche...)"
          required
        />
      </div>

      <button
        type="submit"
        data-testid="contact-form-submit"
        disabled={loading}
        className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#1C1C1C] text-white text-sm tracking-wide hover:bg-stone-800 transition-colors disabled:opacity-60 w-full sm:w-auto"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {loading ? "Wird gesendet..." : "Beratung anfragen"}
      </button>

      <p className="text-xs text-stone-500">
        Mit Absenden stimmen Sie unserer{" "}
        <a href="/datenschutz" className="underline hover:text-stone-800">Datenschutzerklärung</a>{" "}
        zu. Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet.
      </p>
    </form>
  );
}
